/** Client-side file validation for uploads. */
import { UPLOAD_CONFIG } from '../config/awsConfig.ts';
import { kindFromFile } from './media.ts';
import { formatSize } from './media.ts';

export interface ValidationResult {
  valid: File[];
  errors: string[];
}

/** True if the file is an accepted image or video (MIME or extension). */
export function isAcceptedType(file: File): boolean {
  const kind = kindFromFile(file);
  if (kind === 'image') {
    // Accept by detected kind; specific MIME allow-list is advisory.
    return true;
  }
  if (kind === 'video') {
    return true;
  }
  return false;
}

/** Validate one file; returns an error message or null. */
export function validateFile(file: File): string | null {
  if (!isAcceptedType(file)) {
    return `${file.name}: unsupported type${file.type ? ` (${file.type})` : ''}`;
  }
  if (file.size > UPLOAD_CONFIG.MAX_FILE_SIZE) {
    return `${file.name}: too large (${formatSize(file.size)}, max ${formatSize(UPLOAD_CONFIG.MAX_FILE_SIZE)})`;
  }
  return null;
}

/**
 * Partition a list of files into accepted and rejected, de-duplicating by
 * name+size within the batch.
 */
export function validateFiles(files: File[]): ValidationResult {
  const valid: File[] = [];
  const errors: string[] = [];
  const seen = new Set<string>();

  for (const file of files) {
    const dedupeKey = `${file.name}:${file.size}`;
    if (seen.has(dedupeKey)) {
      errors.push(`${file.name}: duplicate in selection (skipped)`);
      continue;
    }
    seen.add(dedupeKey);

    const error = validateFile(file);
    if (error) errors.push(error);
    else valid.push(file);
  }

  return { valid, errors };
}
